import { Position } from '@xyflow/react';

import { NodeType } from './node';

export const NodeConnectionTypes = {
  Main: 'main',
} as const;

export type NodeConnectionType = (typeof NodeConnectionTypes)[keyof typeof NodeConnectionTypes];

export type IConnection = {
  node: string;
  type: NodeConnectionType;
  index: number;
};

export type IConnections = {
  [nodeName: string]: {
    [inputType: string]: IConnection[][];
  };
};

export type CanvasConnection = {
  id: string;
  source: string;
  target: string;
  sourceHandle: string;
  targetHandle: string;
  type: 'default';
  data?: {
    connectionType: NodeConnectionType;
    fromNodeType: string;
    toNodeType: string;
  };
};

export type ConnectionValidationOptions = {
  sourceNodeType?: string;
  targetNodeType?: string;
  sourceConnectionType?: NodeConnectionType;
  targetConnectionType?: NodeConnectionType;
  sourceNodeId?: string;
  targetNodeId?: string;
};

export type IConnectionRule = {
  sourceTypes: string[];
  targetTypes: string[];
  connectionType: NodeConnectionType;
  validate?: (options: ConnectionValidationOptions) => boolean;
};

// Enhanced interfaces for improved connection validation
export type INodeInputFilter = {
  allowedNodeTypes: Array<{
    type: NodeType;
    maxConnections: number;
  }>;
};

export type INodeInputConfiguration = {
  type: NodeConnectionType;
  allowTriggers?: boolean; // if triggers allowed true , other connection won't be able to add .
  filter: INodeInputFilter | undefined;
};

export type INodeOutputConfiguration = {
  type: NodeConnectionType;
  displayName?: string;
  category?: 'error' | 'success' | 'condition';
  maxConnections?: number;
  required?: boolean;
  handlerPosition?: Position;
};

export type ConnectionValidationResult = {
  isValid: boolean;
  error?: string;
  warning?: string;
  suggestion?: string;
};

export type IConnectionCompatibilityRule = {
  sourceConnectionType: NodeConnectionType;
  targetConnectionType: NodeConnectionType;
  isCompatible: boolean;
  requiresAdapter?: boolean;
};

export type ConnectionMetadata = {
  sourceNodeType: string;
  targetNodeType: string;
  connectionType: NodeConnectionType;
  createdAt: Date;
  validated: boolean;
};

// Node type categories for connection validation
export enum NodeTypeCategory {
  TRIGGER = 'trigger',
  AGENT = 'agent',
  LOGIC = 'logic',
  CONDITION = 'condition',
  UTILITY = 'utility',
  OUTPUT = 'output',
}

// Connection validation error types
export enum ConnectionValidationError {
  INCOMPATIBLE_TYPES = 'incompatible_types',
  MAX_CONNECTIONS_EXCEEDED = 'max_connections_exceeded',
  CIRCULAR_DEPENDENCY = 'circular_dependency',
  TRIGGER_AS_TARGET = 'trigger_as_target',
  INVALID_NODE_TYPE = 'invalid_node_type',
  REQUIRED_INPUT_MISSING = 'required_input_missing',
  FILTERED_CONNECTION = 'filtered_connection',
}
